
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { mapActions, mapMutations, mapState } from 'vuex'
import 'vue-slider-component/dist-css/vue-slider-component.css'
import 'vue-slider-component/theme/default.css'
import { mdiPlus, mdiMinus } from '@mdi/js'
import utils from '@/components/tools/utils'
import VuetifyMoney from '@/components/ui/VuetifyMoney'
import DoYouReallyWant from '@/components/account/messages/DoYouReallyWant'

export default {
  components: {
    DoYouReallyWant,
    ValidationObserver,
    ValidationProvider,
    VuetifyMoney
  },
  props: {
    disabled: {
      type: Boolean,
      default: true
    },
    product: {
      type: Object,
      default: undefined
    },
    refreshCallback: {
      type: Function,
      default: () => {
      }
    },
    long: {
      type: Boolean,
      default: true
    }
  },
  data: () => ({
    mdiPlus,
    mdiMinus,
    alert: false,
    alertText: 'Ihr Gebot ist zu niedrig',
    submittingDirectBid: false,
    bid: null,
    bidValid: true
  }),

  computed: {
    directBidLabel () {
      if (this.product?.auction?.status === 'acpt_late_offer') {
        return this.$t('product.set_bid_simple')
      }

      return this.currentPrice ? this.$t('product.set_bid') : this.$t('product.set_initial_bid')
    },

    ...mapState('customer', {
      customerId: state => state.customer?.customerId,
      merchantBlocked: state => state.customer?.merchantBlocked
    }),
    ...mapState('product', {
      status: state => state.data.auction.status,
      renegStatus: state => state.data.auction.renegStatus
    }),

    step () {
      return this.product.auction?.step
    },

    merchantHasFailedLateOfferAlready () {
      return this.product?.context?.hasFailedLateOfferOnProduct === true
    },

    minMaxAllowedBidRule () {
      if (this.product?.auction?.status === 'acpt_late_offer') {
        return undefined
      } else if (this.nextBidPrice && this.maxAllowedBidPrice) {
        return `min_value:${this.nextBidPrice}|max_value:${this.maxAllowedBidPrice}`
      } else if (this.maxAllowedBidPrice) {
        return `max_value:${this.maxAllowedBidPrice}`
      } else {
        return undefined
      }
    },

    currentPrice () {
      return this.product.auction?.currentPrice
    },
    targetPrice () {
      return this.product.auction?.targetPrice
    },
    buyNowPrice () {
      return this.product.auction?.buyNowPrice
    },
    nextBidPrice () {
      return this.product.auction?.nextBidPrice
    },
    maxAllowedBidPrice () {
      return this.product.auction?.maxAllowedBidPrice
    },
    buyNowEnabled () {
      return (
        this.product.auction?.buyNowEnabled &&
        this.product.auction?.buyNowPrice != null &&
        this.product.auction.hasBids === false &&
        this.product.auction.autoRound === false
      )
    },
    auctionId () {
      return this.product.auction.auctionId
    },
    canDecrement () {
      if (this.product?.auction?.status === 'acpt_late_offer') {
        return true
      }

      return this.bid > this.nextBidPrice
    },
    canIncrement () {
      if (this.maxAllowedBidPrice) {
        return this.bid + this.step <= this.maxAllowedBidPrice
      } else {
        return true
      }
    },
    disabledDirectBid () {
      if (this.maxAllowedBidPrice && this.nextBidPrice) {
        return this.disabled || this.bid < this.nextBidPrice || this.bid > this.maxAllowedBidPrice || this.merchantBlocked
      } else {
        return this.disabled || this.merchantBlocked || !this.bid
      }
    },
    bidNote () {
      const key =
         'auction.late_bid_notification'

      return this.$t(key)
    },
    bidNoteHashKey () {
      return '#late-offer'
    },
    lateOfferButtonText () {
      if (this.product?.context?.hasFailedLateOfferOnProduct === true) {
        return this.$t('auction.bidding_not_possible')
      } else if (this.bid) {
        return this.$t('product.place_next_bid', [this.formatCurrency(this.bid)])
      } else {
        return this.$t('product.place_next_bid', [])
      }
    }
  },

  watch: {
    nextBidPrice () {
      this.init()
    }
  },

  mounted () {
    this.init()
  },

  methods: {
    ...mapActions('product', ['getProduct']),
    ...mapMutations('product', ['setProductAuctionAndContext']),
    init () {
      if (this.product?.auction?.status === 'acpt_late_offer') {
        this.bid = null
        return
      }
      if (this.currentPrice === this.maxAllowedBidPrice) {
        this.bid = this.currentPrice
      } else {
        this.bid = this.product.auction.nextBidPrice
      }
    },
    increment () {
      this.bid += this.step
    },
    decrement () {
      this.bid -= this.step
    },

    async validateDirectBid () {
      this.bidValid = await this.$refs.directBidObserver.validate()
    },

    async directBidInput (event) {
      if (this.nextBidPrice && event >= this.nextBidPrice) {
        this.bid = event - event % 100 + this.targetPrice % 100

        await this.validateDirectBid()
      } else if (event >= (this.targetPrice * 0.5)) {
        this.bid = event - event % 100 + this.targetPrice % 100

        await this.validateDirectBid()
      }
    },

    async submitBid () {
      await this.validateDirectBid()

      if (this.bidValid) {
        this.submittingDirectBid = true
        this.alert = false

        const postBidData = {
          price: this.bid
        }

        try {
          const { data, status } = await this.$axios.post(`/private/auction/bid/${this.auctionId}`, postBidData)

          if (status === 200) {
            this.setProductAuctionAndContext(data.product)
          } else if (status === 202) {
            this.alertText = this.$t('customer.context.late_offer_too_low')
            this.alert = true
            await this.refreshCallback()
          } else {
            this.alertText = this.$t('customer.context.overbid')
            this.alert = true
            await this.refreshCallback()
          }
        } catch (error) {
          this.alertText = this.$t('customer.context.overbid')
          this.alert = true
          await this.refreshCallback()
        }

        this.submittingDirectBid = false

        try {
          this.$gtm.push({
            event: 'place_bid',
            auctionId: this.auctionId,
            productId: this.product.entityId,
            value: this.bid,
            currency: 'EUR',
            transaction_id: this.auctionId + '_' + this.bid
          })
        } catch (e) {
          console.error(e)
        }
      }
    },

    async submitBuyNow () {
      this.alert = false

      const postBidData = {
        price: this.bid
      }

      try {
        await this.$axios.post(`/private/auction/buy-now/${this.auctionId}`, postBidData)
      } catch (error) {
        this.alertText = this.$t('auction.you_were_overbid')
        this.alert = true
      }

      await this.refreshCallback()

      try {
        this.$gtm.push({
          event: 'buy_now',
          auctionId: this.auctionId,
          productId: this.productEntityId,
          value: this.buyNowPrice,
          currency: 'EUR',
          transaction_id: this.auctionId + '_' + this.buyNowPrice
        })
      } catch (e) {
        console.error(e)
      }
    },
    formatCurrency (value) {
      return utils.formatCurrency(value)
    },

    async submitLateOffer () {
      this.alert = false

      try {
        const {
          data,
          status
        } = await this.$axios.post(`/private/auction/late-offer/${this.auctionId}`, { price: this.bid })
        if (status === 200) {
          this.setProductAuctionAndContext(data.product)
        } else if (status === 202) {
          this.alertText = this.$t('customer.context.late_offer_too_low')
          this.alert = true
          await this.refreshCallback()
        } else {
          this.alertText = this.$t('customer.context.overbid')
          this.alert = true
          await this.refreshCallback()
        }
      } catch (e) {
        this.alertText = this.getLateOfferErrorMessage(e)
        this.alert = true
      } finally {
        await this.refreshCallback()
      }
    },

    getLateOfferErrorMessage (e) {
      if (!e.response || e.response.status !== 400) {
        return this.$t('error')
      }

      const errorMessageKey = {
        late_offer_unavailable: 'auction.late_offer_unavailable',
        price_too_low: 'customer.context.first_bid_too_low',
        price_too_high: 'auction.bid_too_high'
      }[e.response.data.errorCode]

      return this.$t(errorMessageKey)
    }
  }
}
